/**
 * Schema Builder Page - Clean Design
 * Design content structures with intuitive UI
 */

import { useState } from 'react';
import type { FieldType } from '@tpcms/core';
import { useAppStore } from '../stores';
import { useNavigate } from 'react-router-dom';

const API_URL = '';

const FIELD_TYPES: { value: FieldType; label: string; icon: string; desc: string }[] = [
    { value: 'string', label: 'Short Text', icon: 'Aa', desc: 'Titles, names, single lines' },
    { value: 'text', label: 'Long Text', icon: '¶', desc: 'Descriptions, multi-line text' },
    { value: 'richtext', label: 'Rich Text', icon: '📝', desc: 'Formatting, images, links' },
    { value: 'number', label: 'Number', icon: '123', desc: 'Counts, prices, IDs' },
    { value: 'boolean', label: 'Switch', icon: '🔘', desc: 'Yes/No, True/False' },
    { value: 'date', label: 'Date', icon: '📅', desc: 'Events, birthdays' },
    { value: 'email', label: 'Email', icon: '✉️', desc: 'Valid email addresses' },
    { value: 'url', label: 'URL', icon: '🔗', desc: 'Web links' },
    { value: 'media', label: 'Media', icon: '🖼️', desc: 'Images, Files, Audio' },
];

export function SchemaBuilder() {
    const { currentApp, fetchCollections } = useAppStore();
    const navigate = useNavigate();
    const [collectionName, setCollectionName] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [fields, setFields] = useState<Array<{
        name: string;
        type: FieldType;
        label: string;
        required: boolean;
    }>>([
        { name: 'title', type: 'string', label: 'Title', required: true },
        { name: 'content', type: 'richtext', label: 'Content', required: false }
    ]);

    const addField = (type: FieldType) => {
        const typeInfo = FIELD_TYPES.find(t => t.value === type);
        const name = `field_${fields.length + 1}`;
        setFields([
            ...fields,
            {
                name,
                type,
                label: typeInfo?.label || `Field ${fields.length + 1}`,
                required: false,
            },
        ]);
    };

    const removeField = (index: number) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!collectionName || fields.length === 0) {
            setMessage('Please enter a name and add at least one field.');
            return;
        }

        setSaving(true);
        setMessage('');

        try {
            const response = await fetch(`${API_URL}/api/v1/collections`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    appId: currentApp?.id,
                    name: collectionName,
                    slug: collectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                    fields: fields.map(f => ({
                        name: f.label.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                        type: f.type,
                        label: f.label,
                        required: f.required,
                        unique: false,
                        indexed: false,
                        isPublic: true,
                    })),
                }),
            });

            const result = await response.json();

            if (result.success) {
                setMessage(`✅ Collection "${collectionName}" created successfully!`);
                if (currentApp?.id) {
                    await fetchCollections(currentApp.id);
                }
                setTimeout(() => navigate('/'), 1000);
            } else {
                setMessage(`❌ Error: ${result.error?.message || 'Failed to create collection'}`);
            }
        } catch (error) {
            setMessage(`❌ Error: ${String(error)}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="animate-fade-in max-w-6xl mx-auto space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        Add Content Type
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>
                        Design a new collection for <span className="font-medium" style={{ color: 'var(--accent)' }}>{currentApp?.name}</span>
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-secondary px-6"
                        onClick={() => navigate('/')}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary px-8"
                        disabled={!collectionName || fields.length === 0 || saving}
                        onClick={handleSave}
                    >
                        {saving ? 'Creating...' : 'Save Content Type'}
                    </button>
                </div>
            </div>

            {message && (
                <div className={`p-4 rounded-xl text-sm font-medium ${message.includes('✅') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    {message}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Main Config */}
                <div className="lg:col-span-8 space-y-6">
                    {/* Basic Info */}
                    <div className="card p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                            Collection Info
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="label">Name</label>
                                <input
                                    type="text"
                                    className="input text-lg font-medium"
                                    placeholder="e.g., Blog Posts, Services, Quotes"
                                    value={collectionName}
                                    onChange={(e) => setCollectionName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="label">Access Slug</label>
                                <input
                                    type="text"
                                    className="input bg-opacity-50 text-lg font-mono italic"
                                    placeholder="Auto-generated"
                                    value={collectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}
                                    readOnly
                                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Field Editor */}
                    <div className="card p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                                Fields
                            </h2>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                                {fields.length} Total
                            </span>
                        </div>

                        {fields.length > 0 ? (
                            <div className="space-y-3">
                                {fields.map((field, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-4 p-4 rounded-xl transition-all group"
                                        style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    >
                                        <div className="text-xl" style={{ color: 'var(--text-muted)' }}>
                                            {FIELD_TYPES.find((t) => t.value === field.type)?.icon}
                                        </div>
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                className="bg-transparent border-none outline-none font-medium w-full p-0"
                                                style={{ color: 'var(--text-primary)' }}
                                                value={field.label}
                                                onChange={(e) => {
                                                    const newFields = [...fields];
                                                    newFields[index].label = e.target.value;
                                                    setFields(newFields);
                                                }}
                                            />
                                            <div className="text-[10px] font-bold uppercase tracking-tighter" style={{ color: 'var(--text-muted)' }}>
                                                {field.type}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-gray-400"
                                                    checked={field.required}
                                                    onChange={(e) => {
                                                        const newFields = [...fields];
                                                        newFields[index].required = e.target.checked;
                                                        setFields(newFields);
                                                    }}
                                                />
                                                Required
                                            </label>
                                            <button
                                                onClick={() => removeField(index)}
                                                className="btn btn-ghost p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:text-red-500 transition-all"
                                                title="Remove field"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 rounded-xl border-2 border-dashed" style={{ borderColor: 'var(--border-color)' }}>
                                <div className="text-4xl mb-3">🧩</div>
                                <p style={{ color: 'var(--text-muted)' }}>No fields added. Choose a type from the panel.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar - Add Fields */}
                <div className="lg:col-span-4 space-y-6 h-fit sticky top-6">
                    <div className="card p-6">
                        <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                            Available Fields
                        </h2>
                        <div className="flex flex-col gap-2">
                            {FIELD_TYPES.map((type) => (
                                <button
                                    key={type.value}
                                    onClick={() => addField(type.value)}
                                    className="flex items-center gap-4 p-4 text-left rounded-xl transition-all"
                                    style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                                        e.currentTarget.style.borderColor = 'var(--accent)';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                                        e.currentTarget.style.borderColor = 'var(--border-color)';
                                    }}
                                >
                                    <div className="text-2xl">{type.icon}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{type.label}</div>
                                        <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{type.desc}</div>
                                    </div>
                                    <div className="text-xl" style={{ color: 'var(--text-muted)' }}>+</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
